import { useEffect, useState } from "react";
import { X, Loader2, ShieldCheck, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";
import api from "@/lib/api";

const Mono = ({ children, className = "" }) => (
  <span className={`font-mono uppercase tracking-[0.2em] ${className}`}>{children}</span>
);

const CAT_LABELS = {
  identity: "Event identity", completeness: "Completeness", evidence: "Evidence",
  gps: "GPS integrity", signage: "Acknowledgement signage", cost: "Costings", program: "DRFA program",
};

const Icon = ({ status }) =>
  status === "pass" ? <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" />
    : status === "fail" ? <XCircle className="h-4 w-4 text-red-400 shrink-0 mt-0.5" />
      : <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />;

export default function QaModal({ claimId, onClose }) {
  const [qa, setQa] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/fieldclaim/claims/${claimId}/qa`).then((r) => setQa(r.data)).catch(() => setErr("Could not run the QA check."));
  }, [claimId]);

  const grouped = qa ? qa.checks.reduce((acc, c) => { (acc[c.category] = acc[c.category] || []).push(c); return acc; }, {}) : {};

  return (
    <div className="fixed inset-0 bg-navy-950/85 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} data-testid="qa-modal" className="bg-navy-900 border border-gold-500/30 w-full max-w-2xl p-6 space-y-5 max-h-[92vh] overflow-y-auto text-white">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-xl flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-gold-400" /> Pre-submission QA</h2>
          <button onClick={onClose} data-testid="qa-modal-close"><X className="h-5 w-5 text-white/60" /></button>
        </div>

        {err && <div className="text-red-300 text-sm">{err}</div>}
        {!qa && !err && <div className="flex items-center gap-2 text-white/50 text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Running NSWRA QA checks…</div>}

        {qa && (
          <>
            <div className="grid grid-cols-4 gap-3" data-testid="qa-score-row">
              {[["QA score", `${qa.score}%`, qa.score === 100 ? "text-emerald-300" : qa.score >= 70 ? "text-gold-300" : "text-red-300"],
                ["Failures", qa.failures, qa.failures ? "text-red-300" : "text-emerald-300"],
                ["Warnings", qa.warnings, qa.warnings ? "text-amber-300" : "text-emerald-300"],
                ["Evidence ready", `${qa.readiness_pct}%`, "text-gold-300"]].map(([k, v, c]) => (
                <div key={k} className="border border-gold-500/20 bg-navy-950 px-3 py-3 text-center">
                  <div className={`font-mono text-xl ${c}`}>{v}</div>
                  <Mono className="text-[0.45rem] text-white/40">{k}</Mono>
                </div>
              ))}
            </div>

            <div className="space-y-4" data-testid="qa-checks">
              {Object.entries(grouped).map(([cat, checks]) => (
                <div key={cat}>
                  <Mono className="text-[0.5rem] text-gold-500/70">{CAT_LABELS[cat] || cat}</Mono>
                  <div className="mt-2 space-y-1.5">
                    {checks.map((c, i) => (
                      <div key={i} className="flex items-start gap-2.5 text-sm" data-testid={`qa-check-${cat}-${i}`}>
                        <Icon status={c.status} />
                        <div>
                          <span className={c.status === "pass" ? "text-white/75" : c.status === "fail" ? "text-red-200" : "text-amber-200"}>{c.label}</span>
                          {c.detail && c.status !== "pass" && <p className="text-white/40 text-xs font-light mt-0.5">{c.detail}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {qa.assets.length > 0 && (
              <div>
                <Mono className="text-[0.5rem] text-gold-500/70">Per-asset evidence readiness</Mono>
                <div className="mt-2 space-y-2">
                  {qa.assets.map((a) => (
                    <div key={a.id} className="flex items-center gap-3" data-testid={`qa-asset-${a.id}`}>
                      <span className="text-xs text-white/65 w-40 truncate">{a.name}</span>
                      <span className="font-mono text-[0.55rem] text-gold-400/80 w-10">{a.measure}</span>
                      <div className="flex-1 h-1.5 bg-navy-800 border border-gold-500/10 overflow-hidden">
                        <div className={`h-full ${a.readiness.pct === 100 ? "bg-emerald-400" : "bg-gold-500"}`} style={{ width: `${a.readiness.pct}%` }} />
                      </div>
                      <span className="font-mono text-xs text-white/55 w-10 text-right">{a.readiness.pct}%</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
